import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, CapabilityChip } from "@/components/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Search, CheckCircle2, CircleDashed } from "lucide-react";

export default function Capabilities() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [onlyImplemented, setOnlyImplemented] = useState(false);
  useEffect(() => { api.get("/capabilities").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Skeleton className="h-96 w-full" />;
  const needle = q.toLowerCase();
  const filtered = d.capabilities.filter(
    (c) =>
      (!onlyImplemented || c.implemented) &&
      (c.name.toLowerCase().includes(needle) ||
        c.category.toLowerCase().includes(needle) ||
        (c.telegramMethod || "").toLowerCase().includes(needle))
  );
  const legend = [["Direct", "Bot API, executed immediately"], ["Managed", "Managed Bots / MTProto"], ["Confirmation", "Owner confirms in Telegram"], ["BotFather", "Must be done in @BotFather"]];
  return (
    <div className="space-y-5">
      <PageHeader
        title="Capability Matrix"
        desc={`Telegram Bot API v${d.summary.api_version} (${d.summary.api_version_date}) · MTProto layer ${d.summary.mtproto_layer} · ${d.summary.implemented} of ${d.summary.total} wired to a working adapter.`}
        testId="capabilities-header"
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {legend.map(([chip, desc]) => (
          <div key={chip} className="bg-card border border-border rounded-lg p-3 flex items-start gap-2"><CapabilityChip chip={chip} /><span className="text-xs text-muted-foreground">{desc}</span></div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search capabilities or methods…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="input-search-capabilities" />
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
          <input type="checkbox" checked={onlyImplemented} onChange={(e) => setOnlyImplemented(e.target.checked)} data-testid="toggle-only-implemented" />
          Only capabilities with a live adapter
        </label>
      </div>
      <div className="bg-card border border-border rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-secondary/50 border-b border-border text-xs uppercase text-muted-foreground">
            <th className="px-4 py-3 text-left">Capability</th><th className="px-4 py-3 text-left">Category</th><th className="px-4 py-3 text-left">Telegram method</th><th className="px-4 py-3 text-left">Since</th><th className="px-4 py-3 text-left">Adapter</th><th className="px-4 py-3 text-left">Support</th></tr></thead>
          <tbody>{filtered.map((c) => (
            <tr key={c.name} className="border-b border-border/50" data-testid={`cap-row-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <td className="px-4 py-2.5 font-medium">{c.name}{c.dangerousOperation && <span className="ml-2 text-[10px] text-rose-500 uppercase">danger</span>}</td>
              <td className="px-4 py-2.5 text-muted-foreground">{c.category}</td>
              <td className="px-4 py-2.5 mono text-xs text-primary">{c.telegramMethod || "—"}</td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground mono">{c.apiVersionAdded || "—"}</td>
              <td className="px-4 py-2.5 text-xs">
                {c.implemented ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400" title={c.adapter}>
                    <CheckCircle2 size={13} /> <span className="mono truncate max-w-[220px] inline-block align-bottom">{c.adapter}</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground"><CircleDashed size={13} /> Not wired</span>
                )}
              </td>
              <td className="px-4 py-2.5"><CapabilityChip chip={c.chip} /></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
